<template>
  <div v-if="cartStore.currentOrderData.customfields_data">
      <div v-if="eventId">
        <div class="py-4 px-6 border-t border-surface-grey bg-surface-grey-light font-semibold break-words" v-if="errors && relevantCartTicket && customFieldsByGrouping && Object.keys(customFieldsByGrouping).length > 0" >
            {{ $t('checkout.add_info_to', { name: type === 'upsaleTicket' ? relevantCartTicket?.upsale_title : relevantCartTicket?.name1 }) }}
        </div>
        <div v-for="(fields, grouping) in customFieldsByGrouping" :key="grouping" class="flex flex-col py-6 border-t border-surface-grey">
          <div class="border-l-3 border-primary">
            <div class="px-6 mb-2 font-semibold text-headings break-words">{{ grouping && (grouping == 'tickettype' || grouping == 'event') ? $t('checkout.customfield_group_header_' + grouping) : $t('checkout.customfield_group_header_ticket', { group: grouping })  }}</div>
            <div v-for="field in fields" :key="field.name" class="px-6 py-2">
              <CheckoutCustomField
                  :key="`${field.name}-${customfieldsVersion}`"
                  :type="type"
                  :fieldName="field.name"
                  :ticketId="ticketId"
                  :eventID="eventId.toString()"
                />
            </div>
          </div>
        </div>
      </div>
      <div v-else>
        <div v-for="(group, index) in perEventFieldsByEvent" :key="index" class="flex flex-col py-6 border-t border-surface-grey">
          <div class="border-l-3 border-primary">
            <div class="px-6 mb-2 font-semibold text-headings break-words">{{ group.eventName }}</div>
            <div v-for="field in group.fields" :key="field.name" class="px-6 py-2">
              <CheckoutCustomField
                  :key="`${field.name}-${customfieldsVersion}`"
                  :type="type"
                  :fieldName="field.name"
                  :ticketId="ticketId"
                  :eventID="'per_event'"
                />
            </div>
          </div>
        </div>
      </div>
  </div>
</template>

<script setup lang="ts">

  import { computed, ref, watch } from 'vue';

  import CheckoutCustomField from '@/components/checkout/CheckoutCustomField.vue';
  import { useCartStore } from '@/stores/cart';
  import type { CustomFieldOrderDataEntry, CustomFieldOrderDataName } from '@/types';

  const cartStore = useCartStore();

  const customfieldsVersion = ref(0);
  watch(
    () => cartStore.currentOrderData.customfields_data,
    () => { customfieldsVersion.value++; }
  );

  const props = defineProps<{
    ticketId: number | undefined;
    eventId: number | string | undefined;
    type: 'upsaleTicket' | 'ticket';
    filters: string[];
    errors: string[] | undefined;
  }>();

  //Computed value of cartStore.currentOrderData.customfields_data[eventId], grouped by cartStore.currentOrderData.customfields_data[eventId].grouping and filtered by props.filters & props.ticketId
  const customFieldsByGrouping = computed(() => {
    console.log('Computing customFieldsByGrouping with props:', props);
    if (!cartStore.currentOrderData.customfields_data) return {};
    console.log('Current order data customfields_data:', cartStore.currentOrderData.customfields_data);
    if (!props.eventId) return {};
    console.log('Current event ID:', props.eventId);
    const eventCustomFields: CustomFieldOrderDataName = cartStore.currentOrderData.customfields_data[props.eventId];
    console.log('Custom fields for current event:', eventCustomFields);
    if (!eventCustomFields) return {};
    const filteredFields = Object.values(eventCustomFields).filter(field => field.ticketID == props.ticketId);
    console.log('Filtered custom fields:', filteredFields);
    const groupedFields: Record<string, CustomFieldOrderDataEntry[]> = {};
    filteredFields.forEach(field => {
      const groupingKey = field.grouping || 'default';
      let includeField = false;
      for (const filter of props.filters) {
        if (field.type == filter) {
          includeField = true;
          break;
        } 
      }
      if(props.errors) {
        if(props.errors.includes(field.name)) {
          includeField = true;
        }
        else {
          includeField = false;
        }
      }
      if (includeField) {
        if (!groupedFields[groupingKey]) {
          groupedFields[groupingKey] = [];
        }
        groupedFields[groupingKey].push(field);
      }
    });
    console.log('Initial customFieldsByGrouping:', groupedFields);
    return groupedFields;
  });
  console.log('Initial customFieldsByGrouping:', customFieldsByGrouping.value);

  // Group per_event custom fields by their event_id, with the event name as header
  const perEventFieldsByEvent = computed(() => {
    if (!cartStore.currentOrderData.customfields_data) return [];
    const perEventFields = cartStore.currentOrderData.customfields_data['per_event'];
    if (!perEventFields) return [];

    const grouped: Record<string, CustomFieldOrderDataEntry[]> = {};
    Object.values(perEventFields).forEach(field => {
      if (!props.filters.includes(field.type)) return;
      const eventId = (
        cartStore.customfields.custom_fields.find(f => f.name === field.fieldName) ||
        cartStore.customfields.custom_fields_perticket.find(f => f.name === field.fieldName) ||
        cartStore.customfields.custom_fields_pertickettype.find(f => f.name === field.fieldName)
      )?.event_id?.toString() ?? '__general__';
      if (!grouped[eventId]) grouped[eventId] = [];
      grouped[eventId].push(field);
    });

    return Object.entries(grouped).map(([eventId, fields]) => {
      let eventName: string;
      if (eventId === '__general__') {
        eventName = '';
      } else {
        const cartItem = cartStore.items.find(item => String(item.id) === String(eventId));
        eventName = cartItem?.name || '';
      }
      return { eventName, fields };
    });
  });

  //Computed value of relevant cart item based on provided eventId
  const relevantCartItem = computed(() => {
    if (!cartStore.items) return undefined;
    if (!props.ticketId) return undefined;
    if (!props.eventId) return undefined;
    if (props.eventId != undefined && props.ticketId != undefined) {
      //find the cart item matching the provided eventId
      const eventItem = cartStore.items.find(item => item.id === props.eventId);
      if (!eventItem) return undefined;
      return eventItem;
    } 
    return undefined; 
  });

  //get the relevant ticket based on the provided ticketId from the relevant cart item
  const relevantCartTicket = computed(() => {
    if (!relevantCartItem.value) return undefined;
    if (!props.ticketId) return undefined;
    const ticket = (relevantCartItem.value.tickets ?? []).find(t => t.id === props.ticketId);
    if (!ticket) return undefined;
    return ticket;
  });
</script>
